import { createSelector } from "reselect";
import { createReducer } from "typesafe-actions";

import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";

import EditActivityFocusableField from "../../component/activity/type/EditActivityFocusableField";
import { RootState } from "../rootReducer";

import {
  Actions,
  clearEntityViewReturnState,
  hideEntityView,
  setEntityTypesWithExternalPreview,
  showEntityView,
} from "./actions";
import { EntityViewParentEntity, EntityViewSource } from "./EntityViewPayload";
import RecordPaneReturnState from "./RecordPaneReturnState";

export interface EntityViewState {
  edit?: boolean;
  editValues?: Partial<AnyEntity>;
  elementId?: string;
  entityId?: AnyEntity["id"];
  entityType?: EntityType;
  entityTypesWithExternalPreview: EntityType[];
  focusedFieldName?: EditActivityFocusableField;
  parentEntity?: EntityViewParentEntity;
  requiredFieldsOnly?: boolean;
  returnState?: RecordPaneReturnState;
  source?: EntityViewSource;
  zIndex?: number;
}

const initialState: EntityViewState = {
  entityTypesWithExternalPreview: [],
};

const entityView = createReducer<EntityViewState, Actions>(initialState)
  .handleAction(
    showEntityView,
    (
      state,
      {
        payload: {
          edit,
          editValues,
          elementId,
          entityId,
          entityType,
          focusedFieldName,
          parentEntity,
          requiredFieldsOnly,
          returnState,
          source,
          zIndex,
        },
      }
    ) => ({
      ...state,
      edit,
      editValues,
      elementId,
      entityId,
      entityType,
      focusedFieldName,
      parentEntity,
      requiredFieldsOnly,
      returnState,
      source,
      zIndex,
    })
  )
  .handleAction(hideEntityView, (state) => ({
    ...state,
    editValues: undefined,
    entityId: undefined,
    entityType: undefined,
    focusedFieldName: undefined,
    parentEntity: undefined,
    requiredFieldsOnly: undefined,
    returnState: undefined,
  }))
  .handleAction(clearEntityViewReturnState, (state) => ({ ...state, returnState: undefined }))
  .handleAction(setEntityTypesWithExternalPreview, (state, { payload }) => ({
    ...state,
    entityTypesWithExternalPreview: payload,
  }));

const entityViewState = (state: RootState) => state.entityView;

export const getEntityView = createSelector(entityViewState, (entityView) => entityView);

export const getEntityViewEntityType = createSelector(
  entityViewState,
  ({ entityType }) => entityType
);

export const getEntityViewEntityId = createSelector(entityViewState, ({ entityId }) => entityId);

export const isEntityViewVisible = createSelector(
  entityViewState,
  ({ entityId, entityType }) => !!entityId && !!entityType
);

export const shouldEditPreviewedEntity = createSelector(entityViewState, ({ edit }) => edit);

export const getEditValues = createSelector(entityViewState, ({ editValues }) => editValues);

export const shouldEditRequiredFieldsOnly = createSelector(
  entityViewState,
  ({ requiredFieldsOnly }) => !!requiredFieldsOnly
);

export const getPreviewPaneZIndex = createSelector(entityViewState, ({ zIndex }) => zIndex);

export const getEntityViewElement = createSelector(entityViewState, ({ elementId }) => elementId);

export const getEntityTypesWithExternalPreview = createSelector(
  entityViewState,
  ({ entityTypesWithExternalPreview }) => entityTypesWithExternalPreview
);

export const getEntityViewParentEntity = createSelector(
  entityViewState,
  ({ parentEntity }) => parentEntity
);

export const getEntityViewReturnState = createSelector(
  entityViewState,
  ({ returnState }) => returnState
);

export const getFocusedFieldName = createSelector(
  entityViewState,
  ({ focusedFieldName }) => focusedFieldName
);

export const getEntityViewSource = createSelector(entityViewState, ({ source }) => source);

export type EntityViewActions = Actions;

export default entityView;
